/**
 * Utility function to get geolocation coordinates
 * Caches the result for a short period to avoid repeated calls
 */

let cachedCoords = null;
let cacheTimestamp = null;
const CACHE_DURATION = 1000; // 5 seconds

/**
 * Get current geolocation coordinates
 * @returns {Promise<{latitude: number, longitude: number} | null>}
 */
export function getGeolocation() {
  // Return cached coordinates if still valid
  if (
    cachedCoords &&
    cacheTimestamp &&
    Date.now() - cacheTimestamp < CACHE_DURATION
  ) {
    return Promise.resolve(cachedCoords);
  }

  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    const timeout = setTimeout(() => {
      resolve(null);
    }, 5000); // 5 second timeout

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeout);
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        cachedCoords = coords;
        cacheTimestamp = Date.now();
        resolve(coords);
      },
      () => {
        clearTimeout(timeout);
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 0, // Don't use cached position, always get fresh
      }
    );
  });
}

/**
 * Clear the cached geolocation
 */
export function clearGeolocationCache() {
  cachedCoords = null;
  cacheTimestamp = null;
}
